import React, { useEffect, useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import AuthImage from "@/components/AuthImage";
import { WISHLIST } from "@/constants/testIds";

const EMPTY_FORM = {
  item_name: "",
  collection: "",
  brand: "",
  category: "",
  color: "",
  size: "",
  notes: "",
  where_seen: "",
  where_seen_other: "",
  share_with_club: false,
};

/**
 * WishlistEditor — modal used to create or edit a wishlist item.
 * Renders inside the WishlistPage; submits to /api/wishlist (POST or PATCH).
 */
export default function WishlistEditor({ item, options, collections, onClose }) {
  const isEdit = !!item;
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [photos, setPhotos] = useState([]);  // {photo_id, content_type, original_filename}
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (item) {
      setForm({
        item_name: item.item_name || "",
        collection: item.collection || "",
        brand: item.brand || "",
        category: item.category || "",
        color: item.color || "",
        size: item.size || "",
        notes: item.notes || "",
        where_seen: item.where_seen || "",
        where_seen_other: item.where_seen_other || "",
        share_with_club: !!item.share_with_club,
      });
      setPhotos(item.photos || []);
    } else {
      setForm(EMPTY_FORM);
      setPhotos([]);
    }
  }, [item]);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setError(null);
    setUploading(true);
    try {
      for (const file of Array.from(fileList)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/uploads/photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setPhotos((prev) => [...prev, data]);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removePhoto = (id) => setPhotos((prev) => prev.filter((p) => p.photo_id !== id));

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async () => {
    setError(null);
    // item_name is now optional — a wishlist entry can be a photo + collection.
    setBusy(true);
    try {
      const payload = { ...form };
      if (payload.where_seen !== "Other") payload.where_seen_other = "";
      payload.photo_ids = photos.map((p) => p.photo_id);
      if (isEdit) {
        await api.patch(`/wishlist/${item.wishlist_id}`, payload);
      } else {
        await api.post("/wishlist", payload);
      }
      onClose(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={WISHLIST.editor}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-[#1A1A1A]/70"
    >
      <div className="relative w-full max-w-2xl bg-[#F9F8F4] border border-[#E5E3D8] shadow-2xl">
        <div className="px-8 sm:px-10 pt-8 pb-4 border-b border-[#E5E3D8]">
          <div className="overline" style={{ color: "#B59A5A" }}>Wishlist</div>
          <h2 className="font-serif text-3xl mt-2 text-[#1A422D] leading-tight">
            {isEdit ? "Edit Item" : "Save an Item"}
          </h2>
        </div>

        <div className="px-8 sm:px-10 py-7 max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="mb-5 px-3 py-2 text-sm border border-[#A33] bg-[#fbe9e9] text-[#8a1a1a]">{error}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <label className="label-elegant block mb-2">Item Name <span className="text-[#7A7A7A] text-xs ml-1">(optional)</span></label>
              <input
                type="text"
                value={form.item_name}
                onChange={update("item_name")}
                data-testid={WISHLIST.itemNameInput}
                className="input-field"
                placeholder="e.g. The Albatross — leave blank if you're just saving a photo"
              />
            </div>

            <div>
              <label className="label-elegant block mb-2">Collection</label>
              <input
                list="wishlist-collection-list"
                value={form.collection}
                onChange={update("collection")}
                data-testid={WISHLIST.collectionInput}
                className="input-field"
                placeholder="e.g. Spring Collection"
              />
              <datalist id="wishlist-collection-list">
                {(collections || []).map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="label-elegant block mb-2">Brand</label>
              <input
                list="wishlist-brand-list"
                value={form.brand}
                onChange={update("brand")}
                data-testid={WISHLIST.brandInput}
                className="input-field"
              />
              <datalist id="wishlist-brand-list">
                {(options.brands || []).map((b) => (
                  <option key={b} value={b} />
                ))}
              </datalist>
            </div>

            <div>
              <label className="label-elegant block mb-2">Category</label>
              <select
                value={form.category}
                onChange={update("category")}
                data-testid={WISHLIST.categoryInput}
                className="input-field appearance-none"
              >
                <option value="">— Select —</option>
                {(options.categories || []).map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-elegant block mb-2">Color</label>
              <input
                value={form.color}
                onChange={update("color")}
                data-testid={WISHLIST.colorInput}
                className="input-field"
              />
            </div>

            <div>
              <label className="label-elegant block mb-2">Size</label>
              <input
                value={form.size}
                onChange={update("size")}
                data-testid={WISHLIST.sizeInput}
                className="input-field"
              />
            </div>
            <div>
              <label className="label-elegant block mb-2">Where Did You See It?</label>
              <select
                value={form.where_seen}
                onChange={update("where_seen")}
                data-testid={WISHLIST.whereSeenSelect}
                className="input-field appearance-none"
              >
                <option value="">— Select —</option>
                {(options.where_seen || []).map((w) => (
                  <option key={w} value={w}>{w}</option>
                ))}
              </select>
            </div>

            {form.where_seen === "Other" && (
              <div className="sm:col-span-2">
                <label className="label-elegant block mb-2">Tell us where</label>
                <input
                  value={form.where_seen_other}
                  onChange={update("where_seen_other")}
                  data-testid={WISHLIST.whereSeenOtherInput}
                  className="input-field"
                  placeholder="e.g. Friend's club in Charleston"
                />
              </div>
            )}

            <div className="sm:col-span-2">
              <label className="label-elegant block mb-2">Notes</label>
              <textarea
                rows={3}
                value={form.notes}
                onChange={update("notes")}
                data-testid={WISHLIST.notesInput}
                className="input-field"
                placeholder="Anything that would help the pro shop find it"
              />
            </div>

            <div className="sm:col-span-2 border-t border-[#E5E3D8] pt-5">
              <label className="label-elegant block mb-2">Photos</label>
              <p className="text-xs text-[#7A7A7A] mb-3">
                Add a screenshot, snapshot, or product image so the pro shop knows exactly what you saw.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="wishlist-photo-input"
                className="hidden"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="wishlist-photo-add"
                className="inline-flex items-center gap-2 px-4 py-2.5 text-xs uppercase tracking-wider border border-[#1A422D] text-[#1A422D] hover:bg-[#E9F0EB] transition-colors"
              >
                <ImagePlus size={14} /> {uploading ? "Uploading…" : "Add Photo"}
              </button>
              {photos.length > 0 && (
                <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {photos.map((p) => (
                    <div key={p.photo_id} className="relative group border border-[#E5E3D8] bg-white h-24 overflow-hidden">
                      <AuthImage
                        photoId={p.photo_id}
                        alt={p.original_filename || "Wishlist photo"}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(p.photo_id)}
                        aria-label="Remove photo"
                        className="absolute top-1 right-1 bg-[#1A1A1A]/80 text-[#F9F8F4] w-6 h-6 flex items-center justify-center hover:bg-[#8a1a1a] transition-colors"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="sm:col-span-2 border-t border-[#E5E3D8] pt-5">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.share_with_club}
                  onChange={update("share_with_club")}
                  data-testid={WISHLIST.shareToggle}
                  className="mt-1"
                />
                <span>
                  <span className="block font-serif text-lg text-[#1A1A1A]">Share with the club</span>
                  <span className="block text-xs text-[#7A7A7A] mt-0.5 leading-relaxed max-w-md">
                    Lets the pro shop see this item in club-wide trend reports. Your name and notes are NOT shared — only the item label.
                  </span>
                </span>
              </label>
            </div>
          </div>
        </div>

        <div className="px-8 sm:px-10 py-5 border-t border-[#E5E3D8] flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => onClose(false)}
            data-testid={WISHLIST.cancel}
            disabled={busy}
            className="btn-secondary text-xs"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            data-testid={WISHLIST.save}
            className="btn-primary"
          >
            {busy ? "Saving…" : (isEdit ? "Save Changes" : "Save to Wishlist")}
          </button>
        </div>
      </div>
    </div>
  );
}
